import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Download, History, RefreshCw, Save, Send } from "lucide-react";
import {
  DesignRequestFormFields,
  FORM_VERSION,
  blankSku,
  emptyDesignRequestForm,
} from "../components/DesignRequestForm";
import { useAuth } from "../contexts/AuthContext";
import "../App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const STATUS_LABELS = {
  awaiting_isr_review: "Awaiting Sales Ops Review",
  isr_reviewing: "Sales Ops Reviewing",
  submitted_to_graphics: "Waiting Project Creation",
  converted: "Project Created",
  archived: "Archived",
};

const EVENT_LABELS = {
  submitted: "Form Submitted",
  form_edited: "Form Edited",
  assignment_updated: "Assignment Changed",
  status_updated: "Status Changed",
  artwork_added: "Artwork Added",
  artwork_removed: "Artwork Removed",
  isr_approved: "Sales Ops Approved and Sent to Graphics",
  converted_to_project: "Converted to Project",
  submission_cancelled: "Submission Cancelled",
};

const contactSampleFields = (form) => ({
  sample_recipient_name: form.contact_name || "",
  sample_recipient_email: form.contact_email || "",
  sample_ship_to_street: form.contact_street || "",
  sample_ship_to_city: form.contact_city || "",
  sample_ship_to_state: form.contact_state || "",
  sample_ship_to_zip: form.contact_zip || "",
});

const normalizeForm = (item) => {
  const fields = item.project_fields || {};
  return {
    ...emptyDesignRequestForm,
    ...fields,
    company_name: item.company_name || fields.company_name || "",
    contact_name: item.contact_name || "",
    contact_email: item.contact_email || "",
    contact_phone: item.contact_phone || "",
    contact_street: fields.contact_street || "",
    contact_city: fields.contact_city || "",
    contact_state: fields.contact_state || "",
    contact_zip: fields.contact_zip || "",
    additional_contacts: Array.isArray(fields.additional_contacts) ? fields.additional_contacts : [],
    manufacturer_id: item.manufacturer_id || fields.manufacturer_id || "",
    manufacturer_name: item.manufacturer_name || fields.manufacturer_name || "",
    priority: item.priority || fields.priority || "medium",
    deadline: item.target_due_date || fields.deadline || "",
    isr: item.assigned_isr || fields.isr || "",
    osr: item.assigned_osr || fields.osr || "",
  };
};

const formatFieldLabel = (field = "") => {
  const skuMatch = field.match(/^sku_items\[(\d+)].(.+)$/);
  if (skuMatch) {
    const skuNumber = Number(skuMatch[1]) + 1;
    const child = skuMatch[2].replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
    return `SKU ${skuNumber} - ${child}`;
  }
  return field
    .replace(/^project_fields\./, "")
    .replaceAll("_", " ")
    .replaceAll(".", " - ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatAuditValue = (value) => {
  if (value === null || value === undefined || value === "") return "Blank";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export const IntakeReviewPageV6 = ({
  locked = false,
  maySubmit = true,
  returnPath = "/intake-submissions",
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [item, setItem] = useState(null);
  const [form, setForm] = useState({ ...emptyDesignRequestForm });
  const [skuItems, setSkuItems] = useState([blankSku()]);
  const [attachments, setAttachments] = useState([]);
  const [options, setOptions] = useState({ customers: [], manufacturers: [], isr_list: [], osr_list: [] });
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditNote, setAuditNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const loadAudit = async () => {
    try {
      const response = await axios.get(`${API}/intake-submissions/${id}/audit`, authHeaders());
      setAuditEvents(response.data.events || []);
    } catch {
      setAuditEvents(item?.audit_trail || []);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [submissionResponse, optionResponse] = await Promise.all([
        axios.get(`${API}/intake-submissions/${id}`, authHeaders()),
        axios.get(`${API}/public/intake-options`),
      ]);
      const submission = submissionResponse.data;
      setItem(submission);
      setForm(normalizeForm(submission));
      setSkuItems(submission.sku_items?.length ? submission.sku_items : [blankSku()]);
      setAttachments(submission.attachments || []);
      setOptions(optionResponse.data || {});
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load customer design request");
      navigate("/intake-submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (item) loadAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  const normalizeSku = (sku) => {
    const sampleFields = sku.sample_same_as_contact ? contactSampleFields(form) : sku;
    return {
      ...sku,
      ...sampleFields,
      sample_ship_to_address: [
        sampleFields.sample_ship_to_street,
        sampleFields.sample_ship_to_city,
        sampleFields.sample_ship_to_state,
        sampleFields.sample_ship_to_zip,
      ].filter(Boolean).join(", "),
    };
  };

  const save = async ({ quiet = false } = {}) => {
    setSaving(true);
    try {
      let assignedItem = item;
      if (isAdmin && (form.isr !== (item.assigned_isr || "") || form.osr !== (item.assigned_osr || ""))) {
        const assignmentResponse = await axios.put(
          `${API}/intake-submissions/${id}/assign`,
          { assigned_isr: form.isr || null, assigned_osr: form.osr || null, notify: false },
          authHeaders(),
        );
        assignedItem = assignmentResponse.data;
      }

      const projectFields = {
        ...form,
        customer_id: "",
        isr: assignedItem.assigned_isr || form.isr || "",
        osr: assignedItem.assigned_osr || form.osr || "",
        can_size: form.can_size === "Other" ? form.can_size_other : form.can_size,
      };

      const response = await axios.put(`${API}/intake-submissions/${id}/v4`, {
        company_name: form.company_name,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        priority: form.priority,
        target_due_date: form.deadline,
        notes: form.comments,
        project_fields: projectFields,
        sku_items: skuItems.map(normalizeSku),
        audit_note: auditNote || "Customer design request reviewed and edited",
      }, authHeaders());

      const updated = { ...response.data, attachments };
      setItem(updated);
      setAuditNote("");
      await loadAudit();
      if (!quiet) toast.success("Changes saved and submission revision updated");
      return updated;
    } catch (error) {
      if (!quiet) toast.error(error.response?.data?.detail || "Failed to save changes");
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (!window.confirm("Approve this request and send it to Graphics?")) return;
    try {
      await save({ quiet: true });
      const response = await axios.post(
        `${API}/intake-submissions/${id}/approve-v4`,
        { approval_note: auditNote || "Sales Ops review completed" },
        authHeaders(),
      );
      setItem(response.data);
      await loadAudit();
      toast.success("Request approved and sent to Graphics");
      navigate("/intake-submissions");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve request");
    }
  };

  const uploadFiles = async (files) => {
    if (!files.length) return;
    setUploadingFiles(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const data = new FormData();
        data.append("file", file);
        const response = await axios.post(`${API}/intake-submissions/${id}/artwork`, data, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        });
        uploaded.push(response.data);
      }
      const next = [...attachments, ...uploaded];
      setAttachments(next);
      setItem((previous) => ({ ...previous, attachments: next }));
      await loadAudit();
      toast.success(`${uploaded.length} artwork file${uploaded.length === 1 ? "" : "s"} added`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Artwork upload failed");
    } finally {
      setUploadingFiles(false);
    }
  };

  const removeAttachment = async (index) => {
    const attachment = attachments[index];
    if (!attachment || !window.confirm(`Remove ${attachment.filename} from this request?`)) return;
    try {
      await axios.delete(`${API}/intake-submissions/${id}/artwork/${attachment.id}`, authHeaders());
      const next = attachments.filter((_, attachmentIndex) => attachmentIndex !== index);
      setAttachments(next);
      setItem((previous) => ({ ...previous, attachments: next }));
      await loadAudit();
      toast.success("Artwork removed");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove artwork");
    }
  };

  const download = async (attachment) => {
    try {
      const response = await axios.get(`${API}/intake-attachments/${attachment.id}`, {
        ...authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = attachment.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download artwork");
    }
  };

  if (loading) return <div className="animate-pulse text-muted-foreground">Loading customer design request...</div>;
  if (!item) return null;

  const canApprove = ["awaiting_isr_review", "isr_reviewing"].includes(item.status);

  return (
    <div className="dof-page">
      <style>{`.review-v6-form .dof-version-banner { display: none; }`}</style>
      <div className="dof-appbar">
        <button type="button" onClick={() => navigate(returnPath)} className="dof-back-btn">&larr; Intake Queue</button>
        <div className="text-center">
          <h1 className="dof-appbar-title">REVIEW CUSTOMER DESIGN REQUEST</h1>
          <p className="text-xs text-muted-foreground">{item.confirmation_number} · Form {item.form_version || FORM_VERSION} · Revision {item.submission_revision || 1}</p>
        </div>
        <div />
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <section className="bg-card border border-border rounded-xl p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold">{item.company_name}</h2>
              <p className="text-sm text-muted-foreground">{STATUS_LABELS[item.status] || item.status}</p>
            </div>
            {item.status === "submitted_to_graphics" && (
              <span className="flex items-center gap-2 text-emerald-500"><CheckCircle2 size={18} /> Sales Ops Approved</span>
            )}
          </div>
        </section>

        <form onSubmit={(event) => { event.preventDefault(); if (!locked) save(); }} className="dof-paper review-v6-form" style={{ margin: 0, maxWidth: "none" }}>
          <fieldset disabled={locked} className="contents">
            <DesignRequestFormFields
              form={form}
              setForm={setForm}
              skuItems={skuItems}
              setSkuItems={setSkuItems}
              options={options}
              attachments={attachments}
              uploadingFiles={uploadingFiles}
              onFilesSelected={uploadFiles}
              onRemoveAttachment={removeAttachment}
              artworkPoRequired
            />
          </fieldset>
        </form>

        <section className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-lg font-semibold mb-4">Uploaded Artwork</h3>
          {attachments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No active artwork files.</p>
          ) : (
            <div className="space-y-2">
              {attachments.map((attachment) => (
                <button key={attachment.id} type="button" onClick={() => download(attachment)} className="w-full flex items-center justify-between gap-3 px-3 py-2 bg-secondary rounded-lg text-left">
                  <span>{attachment.filename}</span><Download size={16} />
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-lg font-semibold">Audit Trail</h3>
              <p className="text-sm text-muted-foreground">Append-only history, including submission revisions and Sales Ops approval.</p>
            </div>
            <button type="button" onClick={loadAudit} className="flex items-center gap-2 px-3 py-2 bg-secondary rounded-lg text-sm"><RefreshCw size={15} /> Refresh</button>
          </div>
          {auditEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground">No audit events found.</p>
          ) : (
            <div className="space-y-3">
              {[...auditEvents].reverse().map((event) => (
                <div key={event.id} className="bg-secondary/40 border border-border rounded-lg p-4">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    <History size={14} />
                    <span>{EVENT_LABELS[event.event_type] || event.event_type?.replaceAll("_", " ")}</span>
                    <span className="text-muted-foreground">• {event.user_name || "System"}</span>
                    <span className="text-muted-foreground">• {new Date(event.created_at).toLocaleString()}</span>
                  </div>
                  {event.note && <p className="text-sm mt-2">{event.note}</p>}
                  {(event.changes || []).length > 0 && (
                    <div className="mt-3 overflow-hidden border border-border rounded-lg">
                      {(event.changes || []).map((change, index) => (
                        <div key={`${change.field}-${index}`} className="grid md:grid-cols-[220px_1fr_1fr] gap-2 px-3 py-2 text-xs border-b border-border last:border-0">
                          <strong>{formatFieldLabel(change.field)}</strong>
                          <span><span className="text-muted-foreground">Before:</span> {formatAuditValue(change.old_value)}</span>
                          <span><span className="text-muted-foreground">After:</span> {formatAuditValue(change.new_value)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-card border border-border rounded-xl p-5">
          <label className="block text-sm font-medium mb-2">Reason for Changes / Approval Note</label>
          <textarea
            value={auditNote}
            onChange={(event) => setAuditNote(event.target.value)}
            rows={3}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg"
            placeholder="Optional: explain changes made during review"
          />
        </section>

        <div className="sticky bottom-0 flex flex-wrap justify-end gap-3 bg-card border border-border rounded-xl p-4 shadow-lg">
          <button type="button" onClick={() => navigate(returnPath)} className="px-5 py-2.5 bg-secondary rounded-lg">Cancel</button>
          {!locked && (
            <button type="button" onClick={() => save()} disabled={saving || uploadingFiles} className="flex items-center gap-2 px-5 py-2.5 bg-secondary rounded-lg">
              <Save size={16} /> {saving ? "Saving..." : "Save"}
            </button>
          )}
          {canApprove && maySubmit && !locked && (
            <button type="button" onClick={approve} disabled={saving || uploadingFiles} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-700 text-white rounded-lg">
              <Send size={16} /> Save & Submit to Graphics
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
